import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setAnalyticsUser } from "@/lib/analytics";

type Role = "admin" | "student";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  hasActiveSubscription: boolean;
  signOut: () => Promise<void>;
  refreshAccess: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);

  const loadAccess = async (uid: string) => {
    // Roles
    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", uid);
    setIsAdmin(!!roles?.some((r) => (r.role as Role) === "admin"));

    // Subscription
    const { data: sub } = await supabase
      .from("subscriptions")
      .select("status, current_period_end")
      .eq("user_id", uid)
      .maybeSingle();
    const active =
      sub?.status === "active" &&
      (!sub.current_period_end || new Date(sub.current_period_end) > new Date());
    setHasActiveSubscription(!!active);
  };

  useEffect(() => {
    // 1) Set up listener FIRST
    const { data: subscription } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      setAnalyticsUser(newSession?.user?.id ?? null);
      if (newSession?.user) {
        // Defer DB calls to avoid deadlocks inside the auth callback
        setTimeout(() => loadAccess(newSession.user.id), 0);
      } else {
        setIsAdmin(false);
        setHasActiveSubscription(false);
      }
    });

    // 2) THEN fetch existing session
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      setSession(existing);
      setUser(existing?.user ?? null);
      setAnalyticsUser(existing?.user?.id ?? null);
      if (existing?.user) loadAccess(existing.user.id);
      setLoading(false);
    });

    return () => {
      subscription.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const refreshAccess = async () => {
    if (user) await loadAccess(user.id);
  };

  return (
    <AuthContext.Provider
      value={{ session, user, loading, isAdmin, hasActiveSubscription, signOut, refreshAccess }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
